import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";

export const PageHero = ({
  eyebrow,
  title,
  description,
  highlights = [],
  actions = [],
  visual,
  baseTestId,
  reverse = false,
}) => {
  return (
    <section className="relative overflow-hidden px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal className={reverse ? "lg:order-2" : ""}>
          <div className="space-y-8">
            <div className="space-y-5">
              <p className="eyebrow" data-testid={`${baseTestId}-eyebrow`}>
                {eyebrow}
              </p>
              <h1
                className="font-heading text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl"
                data-testid={`${baseTestId}-title`}
              >
                {title}
              </h1>
              <p
                className="max-w-2xl text-base leading-8 text-slate-600 sm:text-lg"
                data-testid={`${baseTestId}-description`}
              >
                {description}
              </p>
            </div>

            {highlights.length ? (
              <div className="flex flex-wrap gap-3">
                {highlights.map((item) => (
                  <div key={item} className="metric-chip" data-testid={`${baseTestId}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {item}
                  </div>
                ))}
              </div>
            ) : null}

            {actions.length ? (
              <div className="flex flex-col gap-3 sm:flex-row">
                {actions.map((action) => {
                  const isExternal = Boolean(action.href);
                  const content = (
                    <>
                      {action.label}
                      {action.showArrow === false ? null : <ArrowRight className="h-4 w-4" />}
                    </>
                  );

                  return (
                    <Button
                      key={action.label}
                      asChild
                      variant={action.variant || "default"}
                      className={[
                        "rounded-full px-6 py-6 text-sm font-semibold",
                        action.variant === "outline"
                          ? "border-brand-navy/20 text-brand-navy hover:bg-brand-mist"
                          : action.variant === "secondary"
                            ? "bg-brand-teal text-white hover:bg-brand-teal/90"
                            : "bg-brand-navy text-white hover:bg-brand-navy/90",
                      ].join(" ")}
                    >
                      {isExternal ? (
                        <a
                          href={action.href}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={action.testId}
                        >
                          {content}
                        </a>
                      ) : (
                        <Link to={action.to} data-testid={action.testId}>
                          {content}
                        </Link>
                      )}
                    </Button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </Reveal>

        <Reveal className={reverse ? "lg:order-1" : ""} delay={0.1}>
          <div data-testid={`${baseTestId}-visual`} className="relative">
            {visual}
          </div>
        </Reveal>
      </div>
    </section>
  );
};